import { isPopupMode } from 'services/popup/isPopupMode'

// Type definitions
type NavigateFunction = ((page: string | number, ...rest: any[]) => void) | null
type LocationChangedCallback = (location: Location) => void
type UnlistenFunction = () => void

// popup 은 Router 를 마운트하지 않아 navigate=null 이 정상이므로 조용히 무시한다.
// 반면 일반 앱에서 navigate 가 null 이면 실제 버그 신호이므로 경고를 남긴다.
function warnNavigateMissing(method: string): void {
  if (!isPopupMode()) {
    console.warn(`[history] navigate is null; ${method}() ignored (Router not mounted?)`)
  }
}

interface HistoryObject {
  navigate: NavigateFunction
  location: Location
  push: (page: string, ...rest: any[]) => void
  replace: (page: string) => void
  goback: () => void
  back: (step?: number) => void
  locationChangeCBList: LocationChangedCallback[]
  listen: (handleLocationChange: LocationChangedCallback) => UnlistenFunction
  unlisten: (handleLocationChange: LocationChangedCallback) => void
  notifyLocationChange: (location: Location) => void
}

const History: HistoryObject = {
  navigate: null,
  location: window.location,
  // popup(EmailEditorWindow)은 Router 를 마운트하지 않아 navigate 가 null 이다.
  // 4 개 메서드 모두 navigate null 가드로 popup 에서의 throw 를 방지한다.
  push: (page: string, ...rest: any[]) => {
    if (History.navigate == null) {
      warnNavigateMissing('push')
      return
    }
    History.navigate(page, ...rest)
  },
  replace: (page: string) => {
    if (History.navigate == null) {
      warnNavigateMissing('replace')
      return
    }
    History.navigate(page, { replace: true })
  },
  goback: () => {
    if (History.navigate == null) {
      warnNavigateMissing('goback')
      return
    }
    History.navigate(-1)
  },
  back: (step?: number) => {
    if (History.navigate == null) {
      warnNavigateMissing('back')
      return
    }
    const num = step != null ? step : -1
    History.navigate(num)
  },
  locationChangeCBList: [],
  listen: (handleLocationChange: LocationChangedCallback): UnlistenFunction => {
    if (History.locationChangeCBList.indexOf(handleLocationChange) < 0) {
      History.locationChangeCBList.push(handleLocationChange)
    }
    const ret = ((cbFunc: LocationChangedCallback) => {
      return () => {
        History.unlisten(cbFunc)
      }
    })(handleLocationChange)
    return ret
  },
  unlisten: (handleLocationChange: LocationChangedCallback) => {
    const pos = History.locationChangeCBList.indexOf(handleLocationChange)
    if (History.locationChangeCBList.indexOf(handleLocationChange) > -1) {
      History.locationChangeCBList.splice(pos, 1)
    }
  },
  notifyLocationChange: (location: Location) => {
    History.locationChangeCBList.forEach((cbFunc: LocationChangedCallback) => {
      cbFunc(location)
    })
  },
}

export default History
